import * as A from 'astronomy-engine';
import { signal } from '@preact/signals';
import { services } from '../app';
import { upcomingPeaks } from '../astro/meteors';
import { moonPhaseName } from '../astro/solar';
import { starName } from '../data/catalog';
import { FACTS, LessonCtx, Target } from '../data/lessons';
import { Vec3, altAzToEnu, enuToAltAz, refractEnu } from '../lib/math';
import { moonlessSky, roundStars, starsSeen } from '../render/nakedeye';
import { Selection, loadJSON, place, saveJSON, setSimTime, settings, simNow } from '../state';
import { eyeSqm, limitNudge } from './skycheck';
import { crescentDay, lookingFor, lookingText } from './saudi';
import { directionWords, fmtDate, fmtTime, whereInSky } from '../ui/format';

// The live side of lessons and quizzes: find targets in tonight's sky, fill in the numbers that
// change (the Moon, planets, passes, dates), and remember what has been done.

/** Which lesson is playing, and whether the quiz is the harder one. */
export const lessonId = signal<string | null>(null);
export const quizHard = signal(false);

export const learnProgress = signal(loadJSON<{ done: Record<string, number>; bestQuiz: number }>('learn', { done: {}, bestQuiz: 0 }));
export function markLesson(id: string) {
  learnProgress.value = { ...learnProgress.value, done: { ...learnProgress.value.done, [id]: Date.now() } };
  saveJSON('learn', learnProgress.value);
}
export function recordQuiz(score: number) {
  if (score <= learnProgress.value.bestQuiz) return;
  learnProgress.value = { ...learnProgress.value, bestQuiz: score };
  saveJSON('learn', learnProgress.value);
}

/**
 * In daylight, set the clock to half an hour after this evening's darkness begins, so there is a sky to
 * look at (as the guided tour does). Returns the new time, or null if the clock was left alone.
 */
export async function darkFirst(): Promise<Date | null> {
  const e = services.peek()!.engine;
  if (e.context.darkness >= 0.6) return null;
  const dark = A.SearchAltitude(A.Body.Sun, e.frame.observer, -1, simNow(), 1, -12)?.date;
  if (!dark) return null;
  const t = new Date(dark.getTime() + 30 * 60e3);
  setSimTime(t, 1);
  await new Promise((r) => setTimeout(r, 400)); // a few frames for the sky to catch up
  return t;
}

let starIndex: Map<string, number> | null = null;
function starByName(name: string): number | undefined {
  const cat = services.peek()!.engine.stars;
  if (!starIndex) {
    starIndex = new Map();
    for (const [i, info] of cat.info) if (info.name) starIndex.set(info.name.toLowerCase(), i);
  }
  return starIndex.get(name.toLowerCase());
}

/** A lesson target as something the app can select, and where it is. */
export function resolve(t: Target): { sel: Selection | null; enu: Vec3 | null } {
  const e = services.peek()!.engine;
  let sel: Selection | null = null;
  if ('dir' in t) return { sel: null, enu: altAzToEnu(t.alt, t.dir) };
  if ('star' in t) {
    const i = starByName(t.star);
    if (i !== undefined) sel = { kind: 'star', index: i };
  } else if ('body' in t) sel = { kind: 'body', id: t.body };
  else if ('con' in t) {
    const k = e.cons.constellations.findIndex((c) => c.id === t.con);
    if (k >= 0) sel = { kind: 'constellation', index: k };
  } else if ('dso' in t) {
    const m = /^M(\d+)$/.exec(t.dso);
    const k = e.dsos.findIndex((d) => (m ? d.m === Number(m[1]) : d.id === t.dso));
    if (k >= 0) sel = { kind: 'dso', index: k };
  } else if ('sat' in t) sel = { kind: 'satellite', id: t.sat };
  else if ('shower' in t) sel = { kind: 'shower', code: t.shower };
  if (!sel) return { sel: null, enu: null };
  // Constellations are placed at the middle of their figure.
  if (sel.kind === 'constellation') return { sel, enu: e.frame.toEnu(e.consGeo.anchors[sel.index]) };
  return { sel, enu: e.locate(sel)?.enu ?? null };
}

const altAz = (v: Vec3) => enuToAltAz(refractEnu(v));

/** Everything a lesson's text may need, worked out once when the lesson starts. */
export async function lessonContext(): Promise<LessonCtx> {
  const s = services.peek()!;
  const e = s.engine;
  const now = simNow();
  const obs = e.frame.observer;
  const pl = place.peek();
  const set = settings.peek();
  const altOf = (t: Target) => {
    const r = resolve(t);
    return r.enu ? altAz(r.enu).alt : null;
  };
  const moon = e.bodies.find((b) => b.id === 'Moon')!;
  const moonAlt = altAz(e.frame.toEnu(moon.dir)).alt;
  const planetsUp = e.bodies
    .filter((b) => ['Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'].includes(b.id) && b.mag < 2)
    .map((b) => ({ id: b.id, mag: b.mag, alt: altAz(e.frame.toEnu(b.dir)).alt }))
    .filter((b) => b.alt > 8)
    .sort((a, b) => a.mag - b.mag);
  // The sky on a clear night with no Moon, as Naked eye has it here (with the darkness check's fit), and a truly dark one.
  const eye = moonlessSky(set.bortle, eyeSqm(place.peek(), set), limitNudge.peek());
  const nelm = eye.limit;
  const cat = e.stars;
  // The next big shower if one is within three months, otherwise the next one worth watching.
  const peaks = upcomingPeaks(s.meteors.showers, now, 120);
  const peak = peaks.find((p) => (p.shower.zhr ?? 0) >= 50 && p.date.getTime() - now.getTime() < 90 * 86400e3) ?? peaks.find((p) => (p.shower.zhr ?? 0) >= 15);
  let issPass: LessonCtx['issPass'] = null;
  try {
    const passes = await Promise.race([
      s.sats.passes([25544], now, 96, 4.5),
      new Promise<never[]>((r) => setTimeout(() => r([]), 4000)),
    ]);
    const p = passes.find((x) => x.visStart && x.peakEl >= 20 && (x.minMag ?? 9) < 0);
    if (p) issPass = { t: p.visStart!, fromDir: directionWords(p.visStartAz), peakEl: p.peakEl };
  } catch {
    /* no orbit data */
  }
  let nextSolarEclipse: LessonCtx['nextSolarEclipse'] = null;
  try {
    let se = A.SearchLocalSolarEclipse(now, obs);
    for (let k = 0; k < 4 && se.peak.altitude <= 0; k++) se = A.NextLocalSolarEclipse(se.peak.time, obs);
    if (se.peak.altitude > 0) nextSolarEclipse = { date: se.peak.time.date, obscuration: se.obscuration };
  } catch {
    /* none found */
  }
  // The day Saudi Arabia looks for the crescent: the lesson says only that (src/live/saudi.ts).
  const looking = lookingFor(now.getTime(), pl);
  return {
    lat: pl.lat,
    placeName: pl.name,
    alt: altOf,
    where: (t) => {
      const r = resolve(t);
      if (!r.enu) return 'not in view';
      const h = altAz(r.enu);
      return whereInSky(h.alt, h.az).toLowerCase();
    },
    firstUp: (ts) => ts.find((t) => (altOf(t) ?? -90) >= 10) ?? null,
    // On the 29th, the 30th and the 1st, just "Crescent" (src/live/saudi.ts).
    moon: {
      ...(crescentDay(now.getTime(), pl) ? { phase: 'Crescent', pct: null } : { phase: moonPhaseName(A.MoonPhase(now)), pct: Math.round(moon.phaseFraction * 100) }),
      ...(looking ? { looking: lookingText(looking) } : {}),
      up: moonAlt > 0,
      nextFull: A.SearchMoonPhase(180, now, 40)!.date,
      nextNew: ((t) => (crescentDay(t.getTime(), pl) ? null : t))(A.SearchMoonPhase(0, now, 40)!.date),
    },
    planetsUp,
    bortle: set.bortle,
    nelm,
    starsHere: roundStars(starsSeen(eye, cat.mag)),
    starsDark: roundStars(starsSeen(moonlessSky(1, null, 0), cat.mag)),
    nextShower: peak ? { name: peak.shower.name, code: peak.shower.code, date: peak.date, zhr: peak.shower.zhr ?? 0 } : null,
    issPass,
    nextSolarEclipse,
    fmtDate: (d) => fmtDate(d, new Date(d).getFullYear() !== now.getFullYear()),
    fmtTime: (d) => fmtTime(d),
  };
}

// ---------- Quizzes from tonight's sky ----------
export type QuizQ =
  | { kind: 'identify'; prompt: string; sel: Selection; enu: Vec3; fov: number; options: string[]; answer: number; explain: string }
  | { kind: 'direction'; prompt: string; sel: Selection; options: string[]; answer: number; explain: string }
  | { kind: 'point'; prompt: string; name: string; sel: Selection; enu: () => Vec3; radius: number }
  | { kind: 'fact'; prompt: string; options: string[]; answer: number; explain: string };

const shuffle = <T,>(xs: T[]) => {
  const a = [...xs];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

interface Thing { name: string; sel: Selection; enu: Vec3; kind: 'Moon' | 'planet' | 'star' | 'constellation'; fov: number }

/** Bright, well-known things that are up now, to ask about. */
function thingsUp(hard: boolean): Thing[] {
  const e = services.peek()!.engine;
  const out: Thing[] = [];
  const up = (v: Vec3) => altAz(v).alt > 12;
  for (const b of e.bodies) {
    if (b.id === 'Sun' || (b.id !== 'Moon' && b.mag > 1.5)) continue;
    const v = e.frame.toEnu(b.dir);
    if (up(v)) out.push({ name: b.id === 'Moon' ? 'the Moon' : b.id, sel: { kind: 'body', id: b.id }, enu: v, kind: b.id === 'Moon' ? 'Moon' : 'planet', fov: b.id === 'Moon' ? 10 : 30 });
  }
  const cat = e.stars;
  for (const i of cat.named) {
    if (cat.mag[i] > (hard ? 2.1 : 1.3)) continue;
    const v = e.frame.toEnu(e.starDir(i));
    if (up(v)) out.push({ name: starName(cat, i), sel: { kind: 'star', index: i }, enu: v, kind: 'star', fov: 30 });
  }
  e.cons.constellations.forEach((c, k) => {
    if (c.rank > (hard ? 2 : 1)) return;
    const v = e.frame.toEnu(e.consGeo.anchors[k]);
    if (up(v)) out.push({ name: c.name, sel: { kind: 'constellation', index: k }, enu: v, kind: 'constellation', fov: 70 });
  });
  return out;
}

const COMPASS8 = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];

/** Eight questions about the sky as it is now, or fewer if little is up. */
export function buildQuiz(hard: boolean): QuizQ[] {
  const things = shuffle(thingsUp(hard));
  const qs: QuizQ[] = [];
  const nOpts = hard ? 4 : 3;
  const optionsFor = (t: Thing) => {
    const same = things.filter((x) => x !== t && (x.kind === t.kind || (t.kind === 'planet' && x.kind === 'star') || (t.kind === 'star' && x.kind === 'planet')));
    const famous = t.kind === 'constellation' ? ['Orion', 'Cassiopeia', 'Ursa Major', 'Cygnus', 'Leo', 'Scorpius', 'Taurus', 'Gemini'] : ['Sirius', 'Vega', 'Arcturus', 'Capella', 'Betelgeuse', 'Jupiter', 'Venus', 'Polaris'];
    const wrong = shuffle([...new Set([...same.map((x) => x.name), ...famous])].filter((n) => n !== t.name && n.toLowerCase() !== t.name.replace(/^the /, '').toLowerCase())).slice(0, nOpts - 1);
    const opts = shuffle([t.name, ...wrong]);
    return { options: opts.map((o) => o[0].toUpperCase() + o.slice(1)), answer: opts.indexOf(t.name) };
  };
  // Each object is asked about once.
  const used = new Set<Thing>();
  const take = (xs: Thing[], n: number) => {
    const out = xs.filter((x) => !used.has(x)).slice(0, n);
    out.forEach((x) => used.add(x));
    return out;
  };
  // What is this? (the view goes there, with names hidden)
  for (const t of take(things.filter((x) => x.kind !== 'Moon'), 3)) {
    const { options, answer } = optionsFor(t);
    const what = t.kind === 'constellation' ? 'constellation' : 'bright point of light';
    qs.push({
      kind: 'identify', prompt: `What is the ${what} in the circle?`, sel: t.sel, enu: t.enu, fov: t.fov, options, answer,
      explain: t.kind === 'planet' ? `It is ${t.name}, a planet: note how steadily it shines.` : t.kind === 'star' ? `It is ${t.name}.` : `It is ${t.name}.`,
    });
  }
  // Planet or star?
  // A planet if one is up, and a star.
  const planet = take(things.filter((x) => x.kind === 'planet'), 1);
  for (const t of [...planet, ...take(things.filter((x) => x.kind === 'star'), 2 - planet.length)]) {
    qs.push({
      kind: 'identify', prompt: 'Is the bright point in the circle a planet or a star?', sel: t.sel, enu: t.enu, fov: 40,
      options: ['A planet', 'A star'], answer: t.kind === 'planet' ? 0 : 1,
      explain: t.kind === 'planet' ? `A planet: ${t.name}. Planets shine steadily and wander along the ecliptic.` : `A star: ${t.name}. Stars twinkle, especially low down.`,
    });
  }
  // Which way?
  // Which way? Only for things well clear of the zenith, where every direction is nearly right.
  const [d] = take(things.filter((x) => x.kind !== 'constellation' && altAz(x.enu).alt < 65), 1);
  if (d) {
    const az = altAz(d.enu).az;
    const r = Math.round(az / 45) % 8;
    const right = COMPASS8[r];
    // Wrong answers at least 90° away, so one sitting near a boundary is never also right.
    const far = COMPASS8.filter((_, k) => Math.min((k - r + 8) % 8, (r - k + 8) % 8) >= 2);
    const opts = shuffle([right, ...shuffle(far).slice(0, nOpts - 1)]);
    qs.push({ kind: 'direction', prompt: `Which way would you face to see ${d.name} right now?`, sel: d.sel, options: opts.map((o) => o[0].toUpperCase() + o.slice(1)), answer: opts.indexOf(right), explain: `${d.name[0].toUpperCase() + d.name.slice(1)} is ${whereInSky(altAz(d.enu).alt, az).toLowerCase()}.` });
  }
  // Point to it.
  const [p] = take(things.filter((x) => x.kind !== 'constellation'), 1);
  if (p) {
    const e = services.peek()!.engine;
    const sel = p.sel;
    qs.push({ kind: 'point', prompt: `Find ${p.name} and put it in the circle.`, name: p.name, sel, enu: () => e.locate(sel)?.enu ?? p.enu, radius: 4 });
  }
  // True or false.
  for (const f of shuffle(FACTS).slice(0, Math.max(2, 8 - qs.length))) {
    qs.push({ kind: 'fact', prompt: f.s, options: ['True', 'False'], answer: f.fact ? 0 : 1, explain: f.why });
  }
  return shuffle(qs).slice(0, 8);
}
